import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';
import type z from 'zod';

import { accountsSelectors } from 'app-components/flux/accounts';
import {
  selectIsAiBusinessToolsEnabled,
  selectShouldHideBusinessFromAiChat,
} from 'app-components/flux/ai-chat/aiChatSelectors';
import { getBusinessCoaIdsSet, getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import * as tagsActions from 'app-components/flux/tags/tagsActions';
import { getTags } from 'app-components/flux/tags/tagsSelectors';
import { mkTag, type Tag, type TagProps } from 'app-components/flux/tags/tagsTypes';
import * as transactionsSelectors from 'app-components/flux/transactions/transactionsSelectors';
import {
  isInvoiceRelatedTransaction,
  isUnacceptedScheduledTxn,
} from 'app-components/flux/transactions/transactionsUtils';
import { getLogger } from 'app-components/utils/core';
import { ensureArray } from 'app-components/utils/core/immutableHelpers';
import tracker from 'app-components/utils/core/tracker';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../../aiChatUtils';
import { returnToolResponse } from '../../llmToolsFramework';
import type { ToolMessage } from '../../llmToolsTypes';
import {
  AI_TRANSACTION_COA_TYPES,
  type SearchTransactionsArgs,
  searchTransactionsArgsSchema,
} from '../../schemas/transactions';
import {
  buildArAccountIds,
  type CoaInfo,
  type CoaLookupMaps,
  findCategoryIdByName,
  getCoaInfoWithMaps,
  isBusinessTransaction,
  isEstimatedTransaction,
  isInvestmentTransaction,
  isSplitTransaction,
} from '../../utils';

const log = getLogger('llmTransactionTools');
const DEFAULT_MAX_TRANSACTIONS = 50;

const normalizeTagName = (name: string) => name.toLowerCase().trim();

const createTagByName = async (name: string): Promise<TagProps | undefined> => {
  if (!store) return undefined;

  try {
    const response = await actionHelpers.bindPromiseAction<Tag, undefined, { data: { resource: TagProps } }>(
      store.dispatch,
      tagsActions.createTag,
    )(mkTag({ clientId: uuid().toUpperCase(), name }));

    const created = response?.data?.resource;
    return created?.id ? { id: created.id, name: created.name ?? name } : undefined;
  } catch (error) {
    log.error('upsert_transaction: Failed to create tag', { name, error });
    return undefined;
  }
};

const resolveOrCreateTags = async (state: RootState, tagNames: string[]) => {
  const requested = tagNames.reduce<string[]>((acc, name) => {
    const trimmed = name.trim();
    if (trimmed) acc.push(trimmed);
    return acc;
  }, []);

  if (!requested.length) return { tags: [], createdTagIds: [] };

  const tagsByName = getTags(state)?.reduce((acc, tag) => {
    const key = tag.name ? normalizeTagName(tag.name) : '';
    if (key && !acc.has(key)) acc.set(key, tag);
    return acc;
  }, new Map<string, TagProps>());

  const tags: TagProps[] = [];
  const createdTagIds: string[] = [];

  for (const name of requested) {
    const match = tagsByName?.get(normalizeTagName(name));
    if (match) {
      tags.push({ id: match.id, name: match.name });
      continue;
    }

    const created = await createTagByName(name);
    if (created?.id) {
      tags.push(created);
      createdTagIds.push(created.id);
    }
  }

  return tags.length ? { tags, createdTagIds } : undefined;
};

type SearchTransactionResult = {
  txn: ReturnType<typeof transactionsSelectors.getResourcesById> extends ImmutableMap<string, infer T> ? T : never;
  coaInfo: ReturnType<typeof getCoaInfoWithMaps>;
  bankPayee?: string;
  bankMemo?: string;
  matchedTagNames?: string[];
  splitCategories?: string[];
};

const getKeywordMatchedTagNames = (
  txn: SearchTransactionResult['txn'],
  keywordLower: string,
  tagsById: ReturnType<typeof getTags>,
): string[] => {
  const collectTagNames = (tagRefs?: typeof txn.tags | Array<{ id?: string | null }> | null) =>
    ensureArray(tagRefs || []).reduce<string[]>((acc, tagRef) => {
      const tagName = tagsById.get(tagRef.id || '')?.name;
      if (tagName?.toLowerCase().includes(keywordLower) && !acc.includes(tagName)) {
        acc.push(tagName);
      }
      return acc;
    }, []);

  const topLevelMatches = collectTagNames(txn.tags);
  const splitMatches = ensureArray(txn.split?.items || []).reduce<string[]>((acc, item) => {
    collectTagNames(item.tags).forEach((tagName) => {
      if (!acc.includes(tagName)) {
        acc.push(tagName);
      }
    });
    return acc;
  }, []);

  return [...topLevelMatches, ...splitMatches].filter(
    (tagName, index, tagNames) => tagNames.indexOf(tagName) === index,
  );
};

type SplitCoaSummary = {
  coaInfo: CoaInfo;
  splitCategories?: string[];
};

const getSplitAwareCoaSummary = (txn: SearchTransactionResult['txn'], coaMaps: CoaLookupMaps): SplitCoaSummary => {
  if (!isSplitTransaction(txn)) {
    return { coaInfo: getCoaInfoWithMaps(txn.coa, coaMaps) };
  }

  const splitCategories = ensureArray(txn.split?.items || []).reduce<string[]>((acc, item) => {
    const name = getCoaInfoWithMaps(item.coa, coaMaps).name;
    if (name && !acc.includes(name)) acc.push(name);
    return acc;
  }, []);

  return {
    coaInfo: {
      type: 'SPLIT',
      name: splitCategories.length ? `Split: ${splitCategories.join(', ')}` : 'Split',
    },
    splitCategories,
  };
};

const describeAppliedFilters = (args: SearchTransactionsArgs): string => {
  const parts: string[] = [];
  if (args.keyword) parts.push(`keyword="${args.keyword}"`);
  if (args.payee) parts.push(`payee="${args.payee}"`);
  if (args.category) parts.push(`category="${args.category}"`);
  if (args.coaType) parts.push(`coaType=${args.coaType}`);
  if (args.dateFrom) parts.push(`dateFrom=${args.dateFrom}`);
  if (args.dateTo) parts.push(`dateTo=${args.dateTo}`);
  if (args.minAmount !== undefined) parts.push(`minAmount=${args.minAmount}`);
  if (args.maxAmount !== undefined) parts.push(`maxAmount=${args.maxAmount}`);
  if (args.accountId) parts.push(`accountId=${args.accountId}`);
  return parts.join(', ');
};

const buildSearchTransactionsValidationError = (argsValidationError: z.ZodError<SearchTransactionsArgs>): string => {
  const issues = argsValidationError.issues.map((issue) => issue.message).join('; ');
  return `Invalid search_transactions arguments: ${issues}`;
};

const buildEmptySearchTransactionsResponse = (msg: ToolMessage, message: string) =>
  returnToolResponse(msg, {
    success: true,
    count: 0,
    totalMatches: 0,
    limitReached: false,
    transactions: [],
    message,
  });

export const transactionTools = {
  search_transactions: {
    definition: {
      name: 'search_transactions',
      description: `Search and list transactions. NEVER ask clarifying questions or offer options before searching - if the user's request doesn't specify a filter (date range, amount, category, coaType, etc.), OMIT that parameter entirely rather than guessing or inventing a value for it. In particular, never set minAmount or maxAmount unless the user's request explicitly mentions a dollar amount or range - leaving them unset returns all matching transactions regardless of amount.

Use payee when the user wants to find transactions for a specific payee or merchant name (matches both the Payee and bank/statement payee fields). Do NOT put a payee name in the category parameter.

The keyword parameter searches across payee, category name, memo, bank payee, bank memo, tag names, and (for split transactions) each split line's category name. Prefer the dedicated payee or category parameters over keyword when the user's request is clearly about a payee or a category.

Use coaType to filter by transaction type (CATEGORY, TRANSFER, BALANCE_ADJUSTMENT, or UNCATEGORIZED). To find transactions with no category assigned, use coaType: "UNCATEGORIZED" - "Uncategorized" is not a real category name, so passing it via the category parameter is also accepted as an alias for coaType: "UNCATEGORIZED", but coaType is preferred. A transaction matches category/coaType filters if any of its split lines match, even if the transaction itself is split across multiple categories.

Split transactions are returned with coaType "SPLIT", a coaName summarizing the split, and a splitCategories array listing each category involved. They cannot be edited or categorized via chat - if the user wants to change one, tell them to edit it directly in the app.

Invoices, credit memos, and other Accounts Receivable records are not returned here - use get_invoices for those when it is available.

If limit is provided, return only the most recent matching transactions up to that limit. NEVER show accountId, coaId, or id fields to the user. Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          keyword: {
            type: 'string' as const,
            description:
              'Search keyword that matches against payee, category name, memo, bank payee, bank memo, and tag names (case-insensitive partial match).',
          },
          category: {
            type: 'string' as const,
            description:
              'Filter by category name. Use category_list tool first to see available categories. For transactions with no category assigned, prefer coaType: "UNCATEGORIZED" (passing "Uncategorized" here is also accepted as an alias for that). Do not use this for payee/merchant names - use payee instead.',
          },
          payee: {
            type: 'string' as const,
            description:
              'Filter by payee/merchant name (case-insensitive partial match against both the Payee and bank/statement payee fields).',
          },
          coaType: {
            type: 'string' as const,
            enum: [...AI_TRANSACTION_COA_TYPES],
            description: 'Filter by chart of accounts type.',
          },
          dateFrom: {
            type: 'string' as const,
            description: 'Start date for date range filter (YYYY-MM-DD format)',
          },
          dateTo: {
            type: 'string' as const,
            description: 'End date for date range filter (YYYY-MM-DD format)',
          },
          minAmount: {
            type: 'number' as const,
            description:
              'Minimum transaction amount (negative for expenses, positive for income). Only set this if the user explicitly mentions a dollar amount or range - omit both minAmount and maxAmount to search without any amount restriction (e.g. do NOT add these when searching for uncategorized, recent, or generally-described transactions).',
          },
          maxAmount: {
            type: 'number' as const,
            description:
              'Maximum transaction amount (negative for expenses, positive for income). Only set this if the user explicitly mentions a dollar amount or range - see minAmount for guidance.',
          },
          accountId: {
            type: 'string' as const,
            description: 'Filter by specific account ID',
          },
          limit: {
            type: 'integer' as const,
            description: 'Maximum number of results to return (optional).',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatTxnSearch);

      const parsedArgs = safeParse(msg.arguments) ?? {};
      const validationResult = searchTransactionsArgsSchema.safeParse(parsedArgs);

      if (!validationResult.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildSearchTransactionsValidationError(validationResult.error),
        });
      }

      const args = validationResult.data;
      const state = store?.getState() as RootState;

      const { keyword, payee, category, coaType, dateFrom, dateTo, minAmount, maxAmount, accountId, limit } = args;

      log.debug('Searching transactions', args);

      const allTransactions = transactionsSelectors.getResourcesById(state);
      const accountsById = accountsSelectors.getAccountsById(state);
      const categoriesById = getCategoriesById(state);
      const tagsById = getTags(state);
      const coaMaps = { categoriesById, accountsById };
      const limitToolPayloadsEnabled = !!featureFlagsSelectors.getFeatureFlag(state, 'aiChatLimitToolPayloads');
      const excludeBusinessTxns = selectShouldHideBusinessFromAiChat(state);
      const businessCoaIds = getBusinessCoaIdsSet(state);
      const arAccountIds = selectIsAiBusinessToolsEnabled(state) ? buildArAccountIds(state) : null;

      const keywordLower = keyword?.trim().toLowerCase();
      const payeeLower = payee?.trim().toLowerCase();
      const isUncategorizedCategoryAlias = category?.trim().toLowerCase() === 'uncategorized';
      const effectiveCoaType = coaType ?? (isUncategorizedCategoryAlias ? 'UNCATEGORIZED' : undefined);
      const categoryId = category && !isUncategorizedCategoryAlias ? findCategoryIdByName(state, category) : undefined;
      if (category && !isUncategorizedCategoryAlias && !categoryId) {
        log.warn('search_transactions: Category not found', { category });
        return buildEmptySearchTransactionsResponse(
          msg,
          `No transactions found because category "${category}" was not found.`,
        );
      }

      const filteredTransactions = allTransactions.valueSeq().reduce<SearchTransactionResult[]>((acc, txn) => {
        if (
          isInvestmentTransaction(txn) ||
          isEstimatedTransaction(txn) ||
          isUnacceptedScheduledTxn(txn) ||
          (arAccountIds !== null && txn.accountId != null && arAccountIds.has(txn.accountId)) ||
          (excludeBusinessTxns && isBusinessTransaction(txn, businessCoaIds))
        ) {
          return acc;
        }

        const isSplit = isSplitTransaction(txn);
        const splitItems = isSplit ? ensureArray(txn.split?.items || []) : [];
        const { coaInfo, splitCategories } = getSplitAwareCoaSummary(txn, coaMaps);
        const bankPayee = txn.cpData?.payee || txn.cpData?.inferredPayee;
        const bankMemo = txn.cpData?.memo;

        // Split transactions can match a filter through any of their split lines, so every
        // coa-based check below looks across this one set of ids/types/names instead of
        // re-branching on isSplit each time.
        const coaIds = isSplit ? splitItems.map((item) => item.coa?.id) : [txn.coa?.id];
        const coaTypes = isSplit
          ? splitItems.map((item) => getCoaInfoWithMaps(item.coa, coaMaps).type)
          : [coaInfo.type];
        const searchableCoaNames = splitCategories ?? (coaInfo.name ? [coaInfo.name] : []);

        let matchedTagNames: string[] | undefined;

        if (keywordLower) {
          matchedTagNames = getKeywordMatchedTagNames(txn, keywordLower, tagsById);
          const hasFieldMatch = [txn.payee, ...searchableCoaNames, txn.memo, bankPayee, bankMemo].some((value) =>
            value?.toLowerCase().includes(keywordLower),
          );
          if (!hasFieldMatch && matchedTagNames.length === 0) return acc;
        }

        if (payeeLower && ![txn.payee, bankPayee].some((value) => value?.toLowerCase().includes(payeeLower))) {
          return acc;
        }

        const matchesCategory = !categoryId || coaIds.includes(categoryId);
        const matchesCoaType = !effectiveCoaType || coaTypes.includes(effectiveCoaType);
        if (!matchesCategory || !matchesCoaType) return acc;

        if (
          (dateFrom && txn.postedOn < dateFrom) ||
          (dateTo && txn.postedOn > dateTo) ||
          (minAmount !== undefined && txn.amount < minAmount) ||
          (maxAmount !== undefined && txn.amount > maxAmount) ||
          (accountId && txn.accountId !== accountId)
        ) {
          return acc;
        }

        acc.push({ txn, coaInfo, bankPayee, bankMemo, matchedTagNames, splitCategories });
        return acc;
      }, []);

      filteredTransactions.sort((a, b) => b.txn.postedOn.localeCompare(a.txn.postedOn));

      const maxResults = limitToolPayloadsEnabled ? DEFAULT_MAX_TRANSACTIONS : filteredTransactions.length;
      const effectiveLimit = Math.min(limit ?? maxResults, maxResults);
      const exceedsLimit = filteredTransactions.length > effectiveLimit;
      const limitedTransactions = filteredTransactions.slice(0, effectiveLimit);

      const results = limitedTransactions.map(({ txn, coaInfo, bankPayee, bankMemo, splitCategories }) => {
        const account = txn.accountId ? accountsById.get(txn.accountId) : undefined;
        return {
          id: txn.id,
          date: txn.postedOn,
          payee: txn.payee,
          amount: txn.amount,
          accountId: txn.accountId,
          accountName: account?.name,
          coaType: coaInfo.type,
          coaName: coaInfo.name,
          coaId: coaInfo.id,
          ...(splitCategories?.length ? { splitCategories } : {}),
          memo: txn.memo,
          bankPayee,
          bankMemo,
          isReviewed: txn.isReviewed,
        };
      });
      const matchedTagNames = limitedTransactions.reduce<string[]>((acc, result) => {
        result.matchedTagNames?.forEach((tagName) => {
          if (!acc.includes(tagName)) {
            acc.push(tagName);
          }
        });
        return acc;
      }, []);

      log.debug(`Found ${results.length} transactions`);

      const appliedFiltersSummary = describeAppliedFilters(args);
      const isZeroWidthAmountFilter = minAmount === 0 && maxAmount === 0;

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        totalMatches: filteredTransactions.length,
        limitReached: exceedsLimit,
        transactions: results,
        matchedTagNames,
        message: exceedsLimit
          ? `Found ${filteredTransactions.length} matching transactions. Returning the most recent ${results.length}. ${
              limitToolPayloadsEnabled && effectiveLimit === maxResults
                ? 'User can narrow down their search if needed.'
                : 'User can narrow down their search or raise the limit if needed.'
            }`
          : results.length === 0 && appliedFiltersSummary
            ? `Found 0 transactions with filters: ${appliedFiltersSummary}.${
                isZeroWidthAmountFilter
                  ? ' Note: minAmount and maxAmount are both 0, which only matches transactions of exactly $0.00 - remove them if the user did not ask for an amount restriction and re-run the search.'
                  : ' If this seems unexpected, check whether one of these filters is unintentionally too narrow and consider removing it.'
              }`
            : `Found ${results.length} transaction${results.length === 1 ? '' : 's'}.`,
      });
    },
  },

  get_transaction_details: {
    definition: {
      name: 'get_transaction_details',
      description:
        'Get detailed information about a specific transaction by ID. Returns all available fields including chart of accounts info (coaType: CATEGORY/TRANSFER/BALANCE_ADJUSTMENT/UNCATEGORIZED/SPLIT, coaName), the raw split breakdown (if any), tags, attachments, and more. Split transactions cannot be edited or categorized via chat. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          transactionId: {
            type: 'string' as const,
            description: 'The unique ID of the transaction to retrieve',
          },
        },
        required: ['transactionId'],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const state = store?.getState() as RootState;

      const { transactionId } = args;

      if (!transactionId) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Transaction ID is required',
        });
      }

      log.debug('Getting transaction details', { transactionId });

      const allTransactions = transactionsSelectors.getResourcesById(state);
      const transaction = allTransactions.get(transactionId);

      if (!transaction) {
        return returnToolResponse(msg, {
          success: false,
          error: `Transaction not found with ID: ${transactionId}`,
        });
      }

      if (
        isInvestmentTransaction(transaction) ||
        isEstimatedTransaction(transaction) ||
        isUnacceptedScheduledTxn(transaction)
      ) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Investment, estimated, and unaccepted scheduled transactions are not supported.',
        });
      }

      const excludeBusinessTxns = selectShouldHideBusinessFromAiChat(state);
      const businessCoaIds = getBusinessCoaIdsSet(state);
      if (excludeBusinessTxns && isBusinessTransaction(transaction, businessCoaIds)) {
        return returnToolResponse(msg, {
          success: false,
          error:
            'Business transactions (invoices, sales receipts, credit memos, estimates) are not accessible via chat for Quicken Business & Personal.',
        });
      }

      const accountsById = accountsSelectors.getAccountsById(state);
      const categoriesById = getCategoriesById(state);
      const account = transaction.accountId ? accountsById.get(transaction.accountId) : undefined;
      const { coaInfo, splitCategories } = getSplitAwareCoaSummary(transaction, {
        categoriesById,
        accountsById,
      });
      const bankPayee = transaction.cpData?.payee || transaction.cpData?.inferredPayee;
      const bankMemo = transaction.cpData?.memo;

      const details = {
        id: transaction.id,
        date: transaction.postedOn,
        payee: transaction.payee,
        amount: transaction.amount,
        accountId: transaction.accountId,
        accountName: account?.name,
        coaType: coaInfo.type,
        coaName: coaInfo.name,
        coaId: coaInfo.id,
        ...(splitCategories?.length ? { splitCategories } : {}),
        memo: transaction.memo,
        bankPayee,
        bankMemo,
        isReviewed: transaction.isReviewed,
        tags: transaction.tags
          ? ensureArray(transaction.tags).map((tag) => ({
              id: tag.id,
              name: tag.name,
            }))
          : [],
        split: transaction.split && 'toJS' in transaction.split ? transaction.split.toJS() : transaction.split,
        attachments: transaction.attachments || [],
        userFlag: transaction.userFlag,
        userFlagNote: transaction.userFlagNote,
        isExcludedFromReports: transaction.isExcludedFromReports,
        state: transaction.state,
        source: transaction.source,
      };

      log.debug('Transaction details retrieved', { transactionId });

      return returnToolResponse(msg, {
        success: true,
        transaction: details,
      });
    },
  },

  upsert_transaction: {
    definition: {
      name: 'upsert_transaction',
      description: `Create or update a transaction. Opens a dialog for the user to review and confirm. All fields are optional.

If transactionId is provided, updates that transaction. Otherwise creates a new one.
Use search_transactions first to find an existing transaction if the user wants to modify one.

- transactionId: ID of existing transaction to update (from search_transactions)
- payee: Who the transaction is with
- amount: Transaction amount (negative for expenses, positive for income)
- accountId: Account ID for the transaction
- date: Transaction date (YYYY-MM-DD)
- category: Category name
- memo: Additional notes
- tags: Tag names to set on the transaction. This replaces the existing tags, so include any existing tags that should be kept. Pass an empty array to clear all tags. Tags that do not exist yet are created automatically.
- isReviewed: Mark as reviewed (true/false)

Invoices, sales receipts, credit memos, clients, and mileage cannot be created or changed here - those use the dedicated business features in Quicken. If business categories are not available for this user, the tool response says so.

Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          transactionId: {
            type: 'string' as const,
            description: 'ID of existing transaction to update (omit to create new)',
          },
          payee: {
            type: 'string' as const,
            description: 'Payee name',
          },
          amount: {
            type: 'number' as const,
            description: 'Amount (negative for expense, positive for income)',
          },
          accountId: {
            type: 'string' as const,
            description: 'Account ID',
          },
          date: {
            type: 'string' as const,
            description: 'Transaction date (YYYY-MM-DD)',
          },
          category: {
            type: 'string' as const,
            description: 'Category name',
          },
          memo: {
            type: 'string' as const,
            description: 'Transaction memo/notes',
          },
          tags: {
            type: 'array' as const,
            items: { type: 'string' as const },
            description:
              'Tag names, replacing the existing tags. Names that do not exist yet are created. Empty array clears all tags.',
          },
          isReviewed: {
            type: 'boolean' as const,
            description: 'Mark transaction as reviewed',
          },
        },
        required: [],
      },
    },
    handler: async (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const { transactionId, payee, amount, accountId, date, category, memo, tags, isReviewed } = args;

      const state = store?.getState() as RootState;
      const existingTransaction = transactionId
        ? transactionsSelectors.getTransactionById(state, transactionId)
        : undefined;
      const operation = existingTransaction ? 'update' : 'create';

      if (
        existingTransaction &&
        (isInvestmentTransaction(existingTransaction) ||
          isEstimatedTransaction(existingTransaction) ||
          isUnacceptedScheduledTxn(existingTransaction))
      ) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Investment, estimated, and unaccepted scheduled transactions cannot be edited.',
        });
      }

      if (existingTransaction && isSplitTransaction(existingTransaction)) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Split transactions cannot be edited via chat yet. Please edit them directly in the app.',
        });
      }

      const categoryId = category ? findCategoryIdByName(state, category) : undefined;
      if (category && !categoryId) {
        log.warn('upsert_transaction: Category not found by name', {
          category,
        });
      }

      if (
        existingTransaction &&
        selectIsAiBusinessToolsEnabled(state) &&
        isInvoiceRelatedTransaction(existingTransaction)
      ) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Invoices, sales receipts, and credit memos cannot be edited via chat.',
        });
      }

      if (selectShouldHideBusinessFromAiChat(state)) {
        const businessCoaIds = getBusinessCoaIdsSet(state);
        if (existingTransaction && isBusinessTransaction(existingTransaction, businessCoaIds)) {
          return returnToolResponse(msg, {
            success: false,
            error: 'Category changes are not supported on invoices, sales receipts, or credit memos via chat.',
          });
        }
        if (categoryId && businessCoaIds.has(categoryId)) {
          return returnToolResponse(msg, {
            success: false,
            error:
              'Business categories cannot be assigned via chat. Creating invoices, clients, mileage, or other business-only records must be done using the dedicated business features in Quicken.',
          });
        }
      }

      const resolvedTags = Array.isArray(tags) ? await resolveOrCreateTags(state, tags.map(String)) : undefined;

      log.debug('upsert_transaction:', {
        operation,
        transactionId,
        category,
        categoryId,
        tags: resolvedTags?.tags.length,
        createdTagIds: resolvedTags?.createdTagIds,
      });

      return new Promise((resolve) => {
        const callback = (result: { success: boolean; transactionId?: string; error?: string }) => {
          if (result.success) {
            log.debug('upsert_transaction: Success', { operation });
            resolve(
              returnToolResponse(msg, {
                success: true,
                message: `Transaction ${operation}d successfully.`,
              }),
            );
          } else {
            log.error('upsert_transaction: Failed', { error: result.error });
            resolve(
              returnToolResponse(msg, {
                success: false,
                error: result.error || `Failed to ${operation} transaction`,
              }),
            );
          }
        };

        const data = {
          transactionId,
          payee,
          amount,
          accountId,
          date,
          category: categoryId,
          memo,
          tags: resolvedTags?.tags,
          createdTagIds: resolvedTags?.createdTagIds,
          isReviewed,
        };

        const dialogData = mkRootUiData({
          type: 'LLM_TRANSACTION',
          id: `llm-transaction-${operation}-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({ operation, data, callback }),
        });

        log.debug('upsert_transaction: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },
};
